import { useCallback, useEffect, useMemo, useState } from 'react';
import { type Connection, Connections } from '@accounts/domain';
import { Screen } from '@accounts/navigation';
import { usePresence } from '@accounts/session';
import { Refusal } from '@accounts/transport';
import { Callout, EmptyState, LastUpdate, EndLine, ListRow, useConfirm, useRowKeys, Waiting } from '@beyond-js/ui/react';
import { Outage, Problem, Retry, Unsent, useLast, useQuestions } from '@accounts/ui';
import { Details } from './connections/details';
import { listed as phrased } from './connect/permission';

/**
 * AI apps with access (`/connections`): the apps the person allowed to use a Beyond product for them
 * (the family's "AI app"), each row with the product, the organizations it reaches and when it was last used; the row
 * opens the connection's details (`Details`): what it can do there in words, where it answers and when
 * it was connected and expires. **Remove access…** in the row's menu, or **Remove access** in the details,
 * ends the connection and every token of it at once, after the shared confirmation with its
 * consequence; it is never sent
 * again after a renewal of the session (`replay: false`), and `Unsent` says so beside the list when the
 * session had ended. The app's name is the one it gave itself when the person allowed it.
 *
 * The list is the page's one read. When Accounts does not answer it the page says so once (`Outage`,
 * D163): as the page's body while nothing was read, and as a line over the list kept from the last read,
 * which says when that was.
 */
export function ConnectedApps() {
	const { text, date, client, identity, renewed, language } = usePresence();
	// The change sent last, which the person may send again when it was not made (D186)
	const last = useLast();
	const connections = useMemo(() => new Connections(client), [client]);
	const { confirm } = useConfirm(useQuestions());
	const [listed, setListed] = useState<Connection[]>();
	// When the list was last read, said over it while a later read does not answer
	const [at, setAt] = useState<number>();
	const [unread, setUnread] = useState<Refusal>();
	const [failure, setFailure] = useState<unknown>();
	const [unsent, setUnsent] = useState<string>();
	const [ended, setEnded] = useState<string>();
	// What the person removed from an app's access, said once done
	const [narrowed, setNarrowed] = useState<string>();
	// A narrowing the person confirmed, while it is sent: the sheet's action says it works only then
	const [narrowing, setNarrowing] = useState(false);
	const [shown, setShown] = useState<Connection>();

	// Whether Accounts answered, which takes the read out of the page's outage
	const read = useCallback(async () => {
		setUnread(Refusal.kept);
		try {
			setListed(await connections.list());
			setAt(Date.now());
			setUnread(undefined);
			return true;
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return false;
			const given = Refusal.is(error) ? error : new Refusal('INTERNAL', 'Unexpected');
			setUnread(given);
			return !given.unreachable;
		}
	}, [connections]);
	useEffect(() => {
		void read();
	}, [read, renewed]);

	async function disconnect(connection: Connection) {
		const values = { client: connection.client.name, product: connection.product.name };
		const accepted = await confirm({
			title: text('connections.disconnect.title', values),
			message: text('connections.disconnect.body', values),
			accept: text('connections.disconnect.accept'),
			cancel: text('action.cancel'),
			tone: 'danger'
		});
		if (!accepted) return;
		await end(connection);
	}

	/** Keeps only `scopes` after the confirmation with its consequence; never sent again by itself */
	async function narrow(connection: Connection, scopes: string[]) {
		const removes = connection.scopes.filter(scope => !scopes.includes(scope.name));
		const fallback = text('permissions.undescribed', { product: connection.product.name });
		const values = { client: connection.client.name, product: connection.product.name, count: removes.length, list: phrased(removes, language, fallback), permission: phrased(removes, language, fallback) };
		const accepted = await confirm({
			title: text(removes.length === 1 ? 'permissions.confirm.one' : 'permissions.confirm.many', values),
			message: text('permissions.confirm.body', values),
			consequence: { affected: text('permissions.confirm.affected'), lost: text('permissions.confirm.lost', values), recovery: text('permissions.confirm.recovery') },
			accept: text('permissions.confirm.remove'),
			cancel: text('action.cancel'),
			tone: 'danger'
		});
		if (!accepted) return;
		await send(connection, scopes, values);
	}

	async function send(connection: Connection, scopes: string[], values: Record<string, string | number>) {
		last.keep(() => send(connection, scopes, values));
		setFailure(undefined);
		setUnsent(undefined);
		setEnded(undefined);
		setNarrowed(undefined);
		setNarrowing(true);
		try {
			const kept = await connections.narrow(connection, scopes);
			setShown(kept);
			setNarrowed(text('connections.permissions.removed', values));
			await read();
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? connection.id : undefined);
			setFailure(error);
			// Changed or gone since it was shown: what is true now replaces it
			if (Refusal.is(error) && (error.unknown || error.code === 'NOT_FOUND' || error.code === 'ACCESS_CHANGED')) {
				await read();
				setShown(undefined);
			}
		} finally {
			setNarrowing(false);
		}
	}

	async function end(connection: Connection) {
		last.keep(() => end(connection));
		setFailure(undefined);
		setUnsent(undefined);
		setEnded(undefined);
		try {
			await connections.end(connection.id);
			setShown(undefined);
			setEnded(connection.client.name);
			await read();
		} catch (error) {
			if (Refusal.is(error) && error.quiet) return setUnsent(error.unsent ? connection.id : undefined);
			setFailure(error);
			// Without an answer, or for one already gone, the list says what is true now
			if (Refusal.is(error) && (error.unknown || error.code === 'NOT_FOUND')) await read();
		}
	}

	/** The organization a connection is narrowed to, by its name, or all of the person's when it is not */
	const reached = (connection: Connection) => {
		if (!connection.organization) return text('connect.organization.all');
		return identity?.organizations.find(entry => entry.organization === connection.organization)?.name ?? text('connections.organization.left');
	};

	// The product and the organizations are not repeated on rows that all share them (L3); the details say them
	const varied = (pick: (connection: Connection) => string) => (listed?.length ?? 0) < 2 || new Set(listed?.map(pick)).size > 1;
	const products = varied(connection => connection.product.id);
	const reaches = varied(connection => connection.organization ?? '');

	const outage = Boolean(unread?.unreachable);
	// Up and Down move between the rows (L7)
	const keys = useRowKeys();
	return (
		<Screen template="list" width="fluid" title={text('nav.connections')} lead={text('connections.lead')}>
			<Outage whole={outage && !listed} reads={[{ key: 'connections', title: text('nav.connections'), unread, run: read }]} />
			{failure ? <Problem error={failure} again={last.again} check={read} /> : null}
			{ended ? <Callout tone="success" live title={text('connections.disconnected', { client: ended })} /> : null}
			{narrowed ? <Callout tone="success" live title={narrowed} /> : null}
			{!listed && !unread ? <Waiting language={language} /> : null}
			{unread && !outage ? <Retry title={text(unread.key)} label={text('action.retry')} busy={text('state.retrying')} onRetry={read} error={unread} /> : null}
			{listed && outage ? <LastUpdate time={at ?? null} locale={language} labels={LastUpdate.labels[language]} /> : null}
			{listed && !listed.length && (!unread || outage) ? <EmptyState kind="empty" title={text('connections.none')} text={text('connections.none.detail')} labels={EmptyState.labels[language]} /> : null}
			{listed?.length ? (
				<>
					<ul ref={keys} className="bui-rows" data-framed aria-label={text('nav.connections')}>
						{listed.map(connection => (
							<ListRow
								key={connection.id}
								title={connection.client.name}
								run={() => setShown(connection)}
								// A row's facts are one line: the short ones here, the rest in its details
								meta={[
									products ? connection.product.name : null,
									reaches ? reached(connection) : null,
									connection.used ? text('connections.used', { date: date(connection.used) }) : text('connections.created', { date: date(connection.created) })
								]}
								menu={[{ label: text('connections.disconnect.item'), tone: 'danger', onSelect: () => void disconnect(connection) }]}
								labels={ListRow.labels[language]}
							/>
						))}
					</ul>
					<EndLine labels={EndLine.labels[language]} />
				</>
			) : null}
			<Unsent end shown={Boolean(unsent)} />
			<Details connection={shown} reached={reached} onClose={() => setShown(undefined)} onDisconnect={connection => void disconnect(connection)} busy={narrowing} onNarrow={(connection, scopes) => void narrow(connection, scopes)} />
		</Screen>
	);
}
